import { useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { PaymentMethod } from '@/API/models';
import { EditProfileContext } from '@/contexts/EditProfileProvider';
import { fetchFromAPI } from '@/Utils/stripeHooks/helpers';
import { BookingProviderContext } from '@/contexts/BookingProfileContext';
import { Card } from '@stripe/stripe-js';

const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const data = await fetchFromAPI<{ payment_methods: PaymentMethod[] }>(
    '/api/v1/payment_methods',
  );

  return data.payment_methods || [];
};

export const usePaymentMethods = () => {
  return useQuery({
    queryKey: ['paymentMethods'],
    queryFn: fetchPaymentMethods,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
};

const deletePaymentMethod = async (id: string) => {
  return await fetchFromAPI(`/api/v1/payment_methods`, 'DELETE', {
    payment_method_id: id,
  });
};

export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { showAlert } =
    useContext(EditProfileContext) || useContext(BookingProviderContext);

  return useMutation((id: string) => deletePaymentMethod(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['paymentMethods']);
      showAlert({
        type: 'success',
        title: 'Payment method deleted',
        description: 'Payment method was successfully deleted.',
      });
    },
    onError: (error: {error: string}) => {
      showAlert({
        type: 'error',
        title: 'Error',
        description: error?.error,
      });
      
    }
  });
};

// Make Method default
const makeDefaultMethod = async (id: string) => {
  return fetchFromAPI('/api/v1/payment_methods', 'PUT', {
    payment_method_id: id,
  });
};

export const useMakeDefaultMethod = () => {
  const queryClient = useQueryClient();
  const { showAlert } =
    useContext(EditProfileContext) || useContext(BookingProviderContext);

  return useMutation((id: string) => makeDefaultMethod(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['paymentMethods']);
      showAlert({
        type: 'success',
        title: 'Default payment method was changed',
        description: 'Default payment method was successfully updated.',
      });
    },
  });
};

export const useSelectedPaymentMethod = () => {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelectedPayment = (id: string) => {
    setSelected(id);
  };

  return { selected, handleSelectedPayment, setSelected };
};
